import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  useChangeWorkspace,
  useCurrentAccount,
  useFindByQuery,
} from '@gripp/shared-logic';
import { Colors, DropDownView } from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StatusBar, StyleSheet, Text, View } from 'react-native';
import { ActivityIndicator, RadioButton } from 'react-native-paper';
import { useNavigate } from 'react-router';
import { GET_ROLES } from '../../accounts/operations/getRoles';

const MY_HISTORY = 'MY_HISTORY';
const MY_ACTIVITY_LABEL = 'topNav.myHistory';

export const WorkspacesUser = () => {
  const { account } = useCurrentAccount();
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const dropdownRef = useRef<View>(null);
  const { t } = useTranslation();
  const { data } = useFindByQuery({
    modelName: 'role',
    query: GET_ROLES,
  });

  const onPress = () => {
    setDropdownVisible(true);
  };

  if (!account?.allowedWorkspaces || account?.allowedWorkspaces?.length < 1)
    return null;

  return (
    <>
      <Pressable style={styles.container} onPress={onPress} ref={dropdownRef}>
        <Text style={styles.label}>
          {account?.workspace?.name ?? t(MY_ACTIVITY_LABEL)}
        </Text>
        <MaterialCommunityIcons
          name={'chevron-down'}
          size={16}
          color={Colors.black}
        />
      </Pressable>
      {dropdownVisible && (
        <WorkspacesMenu
          buttonRef={dropdownRef}
          isDropdownOpen={dropdownVisible}
          onCloseDropdown={() => setDropdownVisible(false)}
          height={(account?.allowedWorkspaces?.length + 1) * 60 + 40}
          roles={data.items}
        />
      )}
    </>
  );
};

type WorkspacesMenuProps = {
  buttonRef: React.RefObject<View>;
  isDropdownOpen: boolean;
  onCloseDropdown: () => void;
  height: number;
  roles: { id: string; name: string }[];
};

const WorkspacesMenu = (props: WorkspacesMenuProps) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { account, isLoading } = useCurrentAccount();
  const { allowedWorkspaces = [], workspaceRoles } = account || {};
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(
    account?.workspace?.id || MY_HISTORY
  );

  const { mutateAsync: changeWorkspace, isPending } = useChangeWorkspace();

  useEffect(() => {
    if (account?.workspace?.id) setSelectedWorkspaceId(account.workspace.id);
  }, [account]);

  const handleSelectItem = async (wId: string) => {
    setLoading(true);
    setSelectedWorkspaceId(wId);
    if (!account?.id) return;
    const workspaceId = wId === MY_HISTORY ? undefined : wId;
    await changeWorkspace({
      input: {
        id: account?.id,
        workspaceId,
      },
    });
    await queryClient.invalidateQueries({ queryKey: ['currentAccount'] });
    setLoading(false);
    props.onCloseDropdown();
    navigate('/activities', { replace: true });

    // TODO: Figure out query invalidation for gripp admin going to my activity
    window.location.reload();
  };

  const handleSelectorClose = () => {
    props.onCloseDropdown();
  };

  const getRole = (workspaceId: string): string | undefined => {
    const workspaceRole = workspaceRoles?.find(
      ({ workspace }) => workspace === workspaceId
    );
    return props?.roles?.find(({ id }) => id === workspaceRole?.role)?.name;
  };

  return (
    <DropDownView
      anchorRef={props.buttonRef}
      isVisible={props.isDropdownOpen}
      onDismiss={handleSelectorClose}
      position="bottom-right"
      height={props.height > 472 ? 472 : props.height}
      width={370}
    >
      <View style={{ height: props.height > 472 ? 472 : props.height }}>
        <StatusBar hidden={true} />
        <View style={styles.containerDropdown}>
          <View style={styles.modalContent}>
            {isLoading || isPending || loading ? (
              <ActivityIndicator
                animating={true}
                color={Colors.primary}
                size={30}
              />
            ) : (
              [
                { id: MY_HISTORY, name: t(MY_ACTIVITY_LABEL) },
                ...allowedWorkspaces,
              ].map(({ id, name }) => {
                const role = getRole(id);
                return (
                  <Pressable key={id} onPress={() => handleSelectItem(id)}>
                    <View style={styles.markerItemRow}>
                      <View>
                        <Text style={styles.name}>{name}</Text>
                        <Text style={styles.subName}>{role && role}</Text>
                      </View>
                      <RadioButton.Android
                        key={id}
                        value={id}
                        style={styles.item}
                        status={
                          id === selectedWorkspaceId ? 'checked' : 'unchecked'
                        }
                        onPress={() => handleSelectItem(id)}
                      />
                    </View>
                  </Pressable>
                );
              })
            )}
          </View>
        </View>
      </View>
    </DropDownView>
  );
};

const styles = StyleSheet.create({
  containerDropdown: {
    flex: 1,
    flexDirection: 'row',
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: '100%',
    backgroundColor: 'white',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
  },
  modalContent: {
    flex: 1,
    backgroundColor: Colors.primaryGrayBackground,
    paddingHorizontal: 22,
    paddingVertical: 20,
    gap: 12,
    overflow: 'auto',
  },
  item: {
    paddingVertical: 0,
    paddingRight: 4,
    height: 32,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
  },
  name: {
    fontSize: 16,
    fontWeight: '400',
  },
  checkItem: { color: Colors.blackText },
  markerItemRow: {
    width: '100%',
    height: 52,
    maxHeight: 52,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingLeft: 16,
    paddingRight: 4,
    backgroundColor: Colors.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.grayDivider,
  },
  name: {
    fontSize: 16,
  },
  subName: {
    fontSize: 12,
    fontWeight: '500',
    color: Colors.grayText,
  },
});
